import type { SkillId, SkillType, Player } from './types';

export interface SkillDef {
  id: SkillId;
  type: SkillType;
  nameKey: string;
  descKey: string;
  baseValue: number;
  maxValue: number;
  unlockVaultTier: number; // vault tier required to unlock this skill
  cooldownTicks?: number;  // active skills only
}

export const SKILLS: Record<SkillId, SkillDef> = {
  // ── Build 1: Void Executioner ────────────────────────────────
  execute: {
    id: 'execute', type: 'active', unlockVaultTier: 1,
    nameKey: 'execute', descKey: 'execute_desc',
    baseValue: 0.10, maxValue: 0.25, // HP threshold (halved vs bosses)
    cooldownTicks: 0,
  },
  momentum: {
    id: 'momentum', type: 'passive', unlockVaultTier: 1,
    nameKey: 'momentum', descKey: 'momentum_desc',
    baseValue: 0.10, maxValue: 0.30, // bonus special charge % per kill
  },

  // ── Build 2: Aegis Juggernaut ────────────────────────────────
  shield_bash: {
    id: 'shield_bash', type: 'active', unlockVaultTier: 2,
    nameKey: 'shield_bash', descKey: 'shield_bash_desc',
    baseValue: 2.5, maxValue: 4.5, // DEF multiplier for special damage
    cooldownTicks: 8,
  },
  kinetic_absorption: {
    id: 'kinetic_absorption', type: 'passive', unlockVaultTier: 2,
    nameKey: 'kinetic_absorption', descKey: 'kinetic_absorption_desc',
    baseValue: 1, maxValue: 3, // DEF stacks gained per hit received (max 50)
  },

  // ── Build 3: Idle Capitalist ─────────────────────────────────
  market_manipulation: {
    id: 'market_manipulation', type: 'active', unlockVaultTier: 3,
    nameKey: 'market_manipulation', descKey: 'market_manipulation_desc',
    baseValue: 1.5, maxValue: 3.0, // miner yield multiplier (60s duration)
  },
  compounding_interest: {
    id: 'compounding_interest', type: 'passive', unlockVaultTier: 3,
    nameKey: 'compounding_interest', descKey: 'compounding_interest_desc',
    baseValue: 100_000, maxValue: 50_000, // credits per +1% yield (max +50%)
  },

  // ── Build 4: Quantum Looter ──────────────────────────────────
  treasure_hunter: {
    id: 'treasure_hunter', type: 'active', unlockVaultTier: 4,
    nameKey: 'treasure_hunter', descKey: 'treasure_hunter_desc',
    baseValue: 2, maxValue: 3, // boss loot rolls (integer)
  },
  salvage_expert: {
    id: 'salvage_expert', type: 'passive', unlockVaultTier: 4,
    nameKey: 'salvage_expert', descKey: 'salvage_expert_desc',
    baseValue: 0.60, maxValue: 1.00, // salvage return rate
  },

  // ── Ability Skills (ranged / elemental) ─────────────────────
  fireball: {
    id: 'fireball', type: 'active', unlockVaultTier: 1,
    nameKey: 'fireball', descKey: 'fireball_desc',
    baseValue: 3.5, maxValue: 4.5, // damage multiplier vs normal hit
  },
  lightning_bolt: {
    id: 'lightning_bolt', type: 'active', unlockVaultTier: 3,
    nameKey: 'lightning_bolt', descKey: 'lightning_bolt_desc',
    baseValue: 4.0, maxValue: 5.0,
  },
  frost_lance: {
    id: 'frost_lance', type: 'active', unlockVaultTier: 5,
    nameKey: 'frost_lance', descKey: 'frost_lance_desc',
    baseValue: 3.0, maxValue: 4.0,
  },
  frost_nova: {
    // P2.B Bulwark active — AOE 2× ATK with a 5s +30% DEF self-buff and a
    // slow-tag applied to every nearby enemy. Unlocks alongside Bulwark
    // (vault tier 0) so it's accessible from the very first aspect choice.
    id: 'frost_nova', type: 'active', unlockVaultTier: 0,
    nameKey: 'frost_nova', descKey: 'frost_nova_desc',
    baseValue: 2.0, maxValue: 2.8,
  },
  phantom_burst: {
    // P3 Phantom active — high-multiplier strike with guaranteed crit
    // (the special-cast pipeline applies crit-styling on every special;
    // here we lean into it with a higher base multiplier). Mid-range
    // crowd cleanup at 4 targets sits between fireball (5) and
    // lightning_bolt (4).
    id: 'phantom_burst', type: 'active', unlockVaultTier: 4,
    nameKey: 'phantom_burst', descKey: 'phantom_burst_desc',
    baseValue: 2.8, maxValue: 4.2,
  },
  engineer_overload: {
    // P3 Engineer active — highest single-target burst with armor-pen
    // baked in (executeSpecialAttack treats it like fireball's half-def
    // path but at 60 % instead of 50 %). Low crowd-clear (2 targets) by
    // design — Engineer is the sustain build, not the AOE build.
    id: 'engineer_overload', type: 'active', unlockVaultTier: 5,
    nameKey: 'engineer_overload', descKey: 'engineer_overload_desc',
    baseValue: 3.2, maxValue: 4.8,
  },
};

/** Linear interpolation of skill value based on player level (1–100). */
export function skillValue(def: SkillDef, playerLevel: number): number {
  const t = Math.max(0, Math.min(1, (playerLevel - 1) / 99));
  return def.baseValue + (def.maxValue - def.baseValue) * t;
}

/** Returns the unlocked skills for a given highest vault tier. */
export function skillsUnlockedByTier(tier: number): SkillId[] {
  return (Object.values(SKILLS) as SkillDef[])
    .filter((s) => s.unlockVaultTier <= tier)
    .map((s) => s.id);
}

/** Check if player has a skill in either slot — or as the currently-selected
 *  aspect active skill (P2.B). The aspect-driven path is the long-term
 *  replacement for skillSlots; both paths coexist during the transition. */
export function hasSkill(player: Player, id: SkillId): boolean {
  if (player.skillSlots[0] === id || player.skillSlots[1] === id) return true;
  // Aspect-active-skill bridge: the player's chosen aspect skill counts as
  // "equipped" so executeSpecialAttack / auto-execute / chain-extend logic
  // all fire without each having to learn about the aspect system separately.
  if (player.activeSkillId && (player.activeSkillId as string) === id) return true;
  return false;
}

/** Get skill value for player if they have it equipped, else null. */
export function activeSkillValue(player: Player, id: SkillId): number | null {
  if (!hasSkill(player, id)) return null;
  return skillValue(SKILLS[id], player.level);
}
